import {
  getTailorAcquisitionSurface,
  getTailorPosteriorSurface,
  proposeTailorBatch,
  type TailorPosteriorSurfaceResponse,
  type TailorRuntimeProvenance,
  type TailorRuntimeRequest,
} from "../../api/mannequin";
import type {
  CampaignState,
  SamplePoint,
  SamplerDecision,
  TailorExpertName,
} from "./types";
import { validSymmetryPolicies } from "./symmetrySemantics";

const EXPERTS = new Set<TailorExpertName>([
  "value_uncertainty",
  "gradient",
  "topology",
  "downstream",
  "coverage",
]);

export type RealTailorProposal = {
  inputs: Record<string, number>;
  truthOutputs: Record<string, number>;
  predicted: Record<string, { mean: number; sigma: number }>;
  decision: SamplerDecision;
};

function runtimeRequest(
  state: CampaignState,
  observed: SamplePoint[],
  batchSize: number,
  iteration: number,
): TailorRuntimeRequest {
  const sampler = state.config.sampler;
  const symmetries = validSymmetryPolicies(
    state.config.inputs,
    sampler?.symmetries ?? (sampler?.symmetry ? [sampler.symmetry] : []),
  );
  const initialDesignSize = sampler?.initialDesignSize
    ?? Math.max(16, state.config.inputs.filter((axis) => axis.mode === "range").length * 8);
  // Frontier-first schedule ("find the low-alpha nonlinearity first").
  // Stage 1 (< gatePoints adaptive observations): the post-divergence gate
  // holds candidates at/below the frontier inferred FROM OBSERVED SAMPLES
  // (never the oracle) and the post-stall utility is withheld, so budget
  // resolves the break from below. Stage 2: the gate opens and the
  // post-stall utility targets the immediate post-break window at low
  // |beta| — the region the displayed slice needs. The same staged policy
  // feeds proposals AND the acquisition display, so the view mirrors what
  // the sampler is actually doing at that moment.
  const frontier = sampler?.engine === "lhs-mannequin" ? undefined : sampler?.frontierFirst;
  const adaptiveDone = Math.max(0, observed.length - initialDesignSize);
  const frontierStage: 0 | 1 | 2 = frontier == null
    ? 0
    : adaptiveDone < Math.max(1, frontier.gatePoints) ? 1 : 2;
  const basePriority = sampler?.priority ? { ...sampler.priority } : null;
  const priority = frontierStage === 1 && basePriority
    ? { ...basePriority, postStall: undefined }
    : frontierStage === 2 && basePriority?.postStall?.enabled
      ? {
          ...basePriority,
          postStall: { ...basePriority.postStall, windowWidth: 7, lowBetaWidth: 0.25 },
        }
      : basePriority;
  const gateBase = state.config.postDivergenceSampling;
  const postDivergence = frontierStage === 1
    ? {
        ...(gateBase ?? {}),
        enabled: true,
        axisName: gateBase?.axisName ?? "alpha",
        outputName: gateBase?.outputName ?? state.config.outputs[0]?.name ?? "CL",
        minPoints: gateBase?.minPoints ?? 6,
        dropFraction: gateBase?.dropFraction ?? 0.12,
        guardBand: gateBase?.guardBand ?? 1,
      }
    : frontierStage === 2
      ? null
      : gateBase
        ? { ...gateBase }
        : null;
  return {
    vehicle: sampler?.mannequinFamily ?? "cessna_like",
    seed: sampler?.mannequinSeed ?? 7,
    method: "spera",
    // A declared reflection prior is only honest against the clean symmetric
    // oracle. Symmetry-off campaigns may still opt into adversarial features.
    pathologies: symmetries.length > 0 ? "none" : sampler?.mannequinPathologies ?? "sampled",
    // The same aircraft seed produces a repeatable comparison. The engine is
    // mixed separately by Python, so Tailor and LHS receive independent point
    // sequences against exactly the same truth.
    campaign_key: `mannequin-${sampler?.mannequinFamily ?? "cessna_like"}-${sampler?.mannequinSeed ?? 7}`,
    iteration,
    engine: sampler?.engine === "lhs-mannequin" ? "lhs" : "tailor",
    inputs: state.config.inputs.map((axis) => ({
      name: axis.name,
      mode: axis.mode,
      ...(axis.min != null ? { min: axis.min } : {}),
      ...(axis.max != null ? { max: axis.max } : {}),
      ...(axis.fixedValue != null ? { fixed_value: axis.fixedValue } : {}),
    })),
    outputs: state.config.outputs.map((output) => output.name),
    output_weights: Object.fromEntries(
      state.config.outputs.map((output) => [output.name, Math.max(0, output.weight)]),
    ),
    expert_weights: sampler?.tailorWeights
      ? Object.fromEntries(Object.entries(sampler.tailorWeights).filter((entry): entry is [string, number] => Number.isFinite(entry[1])))
      : undefined,
    observations: observed.map((point) => ({ id: point.id, inputs: point.inputs })),
    batch_size: batchSize,
    initial_design_size: initialDesignSize,
    symmetries,
    priority,
    post_divergence: postDivergence,
    candidates_per_step: 600,
  };
}

function mannequinInstanceId(state: CampaignState): string {
  const sampler = state.config.sampler;
  return `mannequin:${sampler?.mannequinFamily ?? "cessna_like"}:${sampler?.mannequinSeed ?? 7}`;
}

export function stampRealTailorRuntime(
  state: CampaignState,
  runtime: TailorRuntimeProvenance,
): void {
  // Surface replay can ask the runtime to refit an earlier checkpoint after a
  // newer proposal block has already completed.  That diagnostic response
  // must not make the live campaign appear to have fallen back to (or stopped
  // at) 128 observations.  Campaign provenance is monotonic in real evidence;
  // a same/newer checkpoint may refresh it, an older one may not.
  const currentTotal = state.samplerRuntime?.totalObservations;
  const incomingTotal = runtime.total_observations;
  if (currentTotal != null && incomingTotal != null && incomingTotal < currentTotal) return;
  const isLhs = state.config.sampler?.engine === "lhs-mannequin";
  state.samplerRuntime = {
    engine: state.config.sampler?.engine ?? "tailor-mannequin",
    mannequinInstanceId: mannequinInstanceId(state),
    label: isLhs ? "Python blockwise LHS playback · Mannequin oracle" : "Tailor five-expert response portfolio · Mannequin oracle",
    backendEngine: runtime.engine,
    surrogate: runtime.surrogate,
    navigatorSha256: runtime.navigator.sha256,
    navigatorArchitecture: runtime.navigator.architecture,
    policySource: runtime.policy_source ?? undefined,
    totalObservations: runtime.total_observations,
    fitObservations: runtime.fit_observations,
    approximationMode: runtime.approximation_mode,
    activeSetCap: runtime.active_set_cap,
    effectiveFitRows: runtime.effective_fit_rows,
    totalTransportObservations: runtime.total_transport_observations,
    returnedTransportObservations: runtime.returned_transport_observations,
    blockSize: runtime.block_size,
    fallback: runtime.fallback,
  };
}

function selectedBy(value: string): SamplerDecision["selectedBy"] {
  if (value === "initial_design") return value;
  return EXPERTS.has(value as TailorExpertName)
    ? value as TailorExpertName
    : "initial_design";
}

export async function proposeRealTailorBatch(
  state: CampaignState,
  observed: SamplePoint[],
  batchSize: number,
  iteration: number,
): Promise<RealTailorProposal[]> {
  const response = await proposeTailorBatch(runtimeRequest(state, observed, batchSize, iteration));
  stampRealTailorRuntime(state, response.runtime);
  const isLhs = state.config.sampler?.engine === "lhs-mannequin";
  return response.proposals.map((proposal) => ({
    inputs: proposal.inputs,
    truthOutputs: proposal.truth_outputs,
    predicted: proposal.predicted,
    decision: {
      engine: isLhs ? "lhs-mannequin" : "tailor-mannequin",
      label: isLhs ? "Python blockwise LHS playback" : "Tailor five-expert response portfolio",
      selectedBy: selectedBy(proposal.decision.selected_by),
      reason: proposal.decision.reason,
      expertScores: proposal.decision.expert_scores
        .filter((expert) => EXPERTS.has(expert.name as TailorExpertName))
        .map((expert) => ({ ...expert, name: expert.name as TailorExpertName })),
      totalScore: proposal.decision.total_score ?? undefined,
      priorityScore: proposal.decision.priority_score ?? undefined,
      priorityStrength: proposal.decision.priority_strength ?? undefined,
      prioritySummary: proposal.decision.priority_summary ?? undefined,
      ardAxisRelevance: proposal.decision.ard_axis_relevance,
      mannequinInstanceId: mannequinInstanceId(state),
    },
  }));
}

export async function fetchRealTailorSurface(
  state: CampaignState,
  observed: SamplePoint[],
  output: string,
  xAxis: string,
  yAxis: string,
  held: Record<string, number>,
  resolution: number,
  transportAxes?: string[],
  selectionHeld?: Record<string, number>,
  transportMonteCarlo?: boolean,
  transportMcMembers?: number,
  signal?: AbortSignal,
): Promise<TailorPosteriorSurfaceResponse> {
  const response = await getTailorPosteriorSurface({
    ...runtimeRequest(state, observed, 1, Math.max(1, state.iteration)),
    output,
    x_axis: xAxis,
    y_axis: yAxis,
    held,
    resolution,
    transport_axes: transportAxes,
    selection_held: selectionHeld,
    transport_monte_carlo: transportMonteCarlo || undefined,
    transport_mc_members: transportMcMembers || undefined,
  }, signal);
  stampRealTailorRuntime(state, response.runtime);
  return response;
}

export async function fetchRealTailorAcquisition(
  state: CampaignState,
  observed: SamplePoint[],
  xAxis: string,
  yAxis: string,
  held: Record<string, number>,
  resolution: number,
): Promise<{ x: number[]; y: number[]; score: number[][] }> {
  const response = await getTailorAcquisitionSurface({
    ...runtimeRequest(state, observed, 1, Math.max(1, state.iteration)),
    x_axis: xAxis,
    y_axis: yAxis,
    held,
    resolution,
  });
  stampRealTailorRuntime(state, response.runtime);
  return response;
}
